import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  AttendanceOverview,
  Holiday,
  HolidayType,
  MarkEntry,
  MarkResult,
  Register,
  SectionMonth,
  StudentAttendance,
} from './types'

export const attendanceKeys = {
  all: ['attendance'] as const,
  register: (sectionId: number, date: string) =>
    [...attendanceKeys.all, 'register', sectionId, date] as const,
  overview: (date: string) => [...attendanceKeys.all, 'overview', date] as const,
  month: (sectionId: number, month: string) => [...attendanceKeys.all, 'month', sectionId, month] as const,
  holidays: () => [...attendanceKeys.all, 'holidays'] as const,
  family: (studentId: number) => [...attendanceKeys.all, 'family', studentId] as const,
}

export const registerQuery = (sectionId: number, date: string) =>
  queryOptions({
    queryKey: attendanceKeys.register(sectionId, date),
    queryFn: () => api.get<Register>(`/attendance/sections/${sectionId}/register`, { params: { date } }),
  })

export const overviewQuery = (date: string) =>
  queryOptions({
    queryKey: attendanceKeys.overview(date),
    queryFn: () => api.get<AttendanceOverview>('/attendance/overview', { params: { date } }),
  })

export const sectionMonthQuery = (sectionId: number, month: string) =>
  queryOptions({
    queryKey: attendanceKeys.month(sectionId, month),
    queryFn: () => api.get<SectionMonth>(`/attendance/sections/${sectionId}/history`, { params: { month } }),
  })

export const holidaysQuery = () =>
  queryOptions({
    queryKey: attendanceKeys.holidays(),
    queryFn: () => api.get<Holiday[]>('/attendance/holidays'),
  })

export const familyAttendanceQuery = (studentId: number) =>
  queryOptions({
    queryKey: attendanceKeys.family(studentId),
    queryFn: () => api.get<StudentAttendance>(`/me/family/students/${studentId}/attendance`),
  })

export function useMarkAttendance(sectionId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { date: string; entries: MarkEntry[] }) =>
      api.put<MarkResult>(`/attendance/sections/${sectionId}/register`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: attendanceKeys.all })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export interface HolidayInput {
  id?: number
  date: string
  name: string
  type: HolidayType
}

export function useSaveHoliday() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: HolidayInput) =>
      id
        ? api.put<Holiday>(`/attendance/holidays/${id}`, body)
        : api.post<Holiday>('/attendance/holidays', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: attendanceKeys.all }),
  })
}

export function useDeleteHoliday() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/attendance/holidays/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: attendanceKeys.all }),
  })
}
